import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import { t } from "@lingui/macro";
import { includes } from "lodash-es";
import { useRouter } from "next/router";
import {
  localeFix,
  localeFixOptional,
} from "src/utils/genderLocale/genderLocale";
import { PageHead } from "src/molecules/PageHead";
import { Modal } from "src/atoms/Modal";
import { Skeleton } from "src/atoms/Skeleton";
import { ProductOrder } from "src/organisms/ProductOrder";
import { OutOfStock } from "src/molecules/OutOfStock";
import { ProductBreadcrumbs } from "src/molecules/ProductBreadcrumbs";
import { ProductInfo } from "src/molecules/ProductInfo";
import { ProductMeta } from "src/molecules/ProductMeta";
import { ProductPhotos } from "src/molecules/ProductPhotos";
import { ProductSidebarSkeleton } from "src/molecules/ProductSideBarSkeleton";
import { localeGenderMap } from "const/localeGenderMap";
import {
  ProductGender,
  ProductVariation,
  PromotionDisplayType,
  PromotionType,
  SeoQueryResponse,
  ShopType,
  SingleProduct,
} from "src/types/graphql.d";
import { SizeGuide } from "src/organisms/SizeGuide";
import { useSegment } from "src/hooks/useSegment";
import { StyledProductView } from "./ProductView.styled";
import styles from "./ProductView.module.scss";
import { googleTagManager } from "src/tracking/googleTagManager";
import { ViewedProductState } from "src/stores/app/app.store";
import { locale } from "src/config/app-config";
import { useAppActions } from "src/hooks/stores/use-app-store";
import { useIncreaseCartItem } from "src/hooks/useIncreaseCartItem";
import ErrorPage from "src/pages/_error";
import currencyMap from "src/utils/currencyMap";
import { useCartData } from "src/hooks/stores/use-cart-store";
import AddToCollection from "src/organisms/AddToCollection/AddToCollection";
import ProductViewCarousels from "src/organisms/ProductView/ProductViewCarousels";
import { SEGMENT_ENGAGEMENT_TIMEOUT } from "src/segment";
import { trackProductViewedEvent } from "src/tracking/events/product-viewed";
import { trackProductEngagedEvent } from "src/tracking/events/product-engaged";
import { useLingui } from "@lingui/react";
import NoSSR from "src/atoms/NoSSR";
import { PromoTimer } from "src/molecules/PromoTimer";
import { PromotionalRibbon } from "../PromotionalRibbon";
import { Flash } from "src/icons/Flash";
import { PromotionalBlock } from "src/molecules/PromotionalBlock";
import { usePromotion } from "src/hooks/usePromotion";
import { usePromotionContext } from "src/hooks/usePromotionContext";
import { getCTAttribute } from "src/utils/ct/attributeHelper";
import { LoginRegisterModal } from "src/molecules/LoginRegisterModal";
import {
  deleteSessionStorageItem,
  setSessionStorageJSON,
} from "src/utils/helper/sessionStorage";
import {
  GATED_POPUP_REDIRECT_DATA,
  GATED_PRODUCT_PAGE,
} from "src/constants/gatedItem";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { ApolloError } from "@apollo/client";
import { getCartInfo } from "src/utils/ct/cart-helper";
import {
  usePageViewedContext,
  usePageViewedDispatch,
} from "src/hooks/context/use-page-viewed-context";
import {
  useShopTypeContext,
  useShopTypeDispatch,
} from "src/hooks/context/use-shop-type-context";
import Cookies from "js-cookie";
import { ImageCarouselContextProvider } from "src/contexts/image-carousel.context";
import { OT_SHOP_TYPE_COOKIE } from "src/utils/constants";
import { useSizeSchemaFilter } from "src/hooks/useSizeSchemaFilter";
import { getPdpResultId } from "src/utils/listing-persistence";
import { getLookbookImageUrl } from "src/utils/lookbook-image-loader";
interface Props {
  slug: string;
  productLoading: boolean;
  productError: ApolloError | string | null;
  product?: SingleProduct;
  seoContent?: SeoQueryResponse | null;
}

const ProductView: FC<Props> = ({
  slug,
  productLoading,
  productError,
  product,
  seoContent,
}) => {
  const { i18n } = useLingui();
  const router = useRouter();
  // Constructor result_id of the listing the user came from (PLP → PDP handoff),
  // so PDP tracking can attribute the originating search/browse result.
  const resultId = getPdpResultId(slug);
  const { data: session } = useSession();
  const { isLoggedIn, isSessionLoading } = useNewIsLoggedIn();
  const { pageViewTracked } = usePageViewedContext();
  const pageViewedDispatch = usePageViewedDispatch();
  const { currentShopType } = useShopTypeContext();
  const shopTypeDispatch = useShopTypeDispatch();

  const path = router.asPath;

  const [size, setSize] = useState<ProductVariation | null>();
  const [selectedSizeSchema, setSelectedSizeSchema] = useState<string>();
  const [isOpenSizeGuide, setIsOpenSizeGuide] = useState(false);
  const { cart, loading: isCartLoading } = useCartData();
  const sizeGuideRef = useRef<HTMLDivElement>(null);
  const timerID = useRef<number>();
  const localeGenderId =
    currentShopType &&
    (localeGenderMap[locale] as Record<string, string>)[currentShopType];
  const [isLoginRegisterModalOpen, setIsLoginRegisterModalOpen] =
    useState(false);
  const [actionSource, setActionSource] = useState<string>("");
  const [addToCartVariationId, setAddToCartVariationId] = useState<string>();

  const promoShopType = localeFixOptional({ gender: currentShopType });

  const { data: promoData } = usePromotion(
    slug,
    PromotionType.Product,
    promoShopType as ShopType
  );
  const { promotionContextDispatch, promotionContextState } =
    usePromotionContext();

  // variationId contains sku number
  const selectedVariationId = useRef<string>();
  const { setViewedProductState, setAddedProdToCart, setPageViewedProperties } =
    useAppActions();

  const currency = currencyMap[locale] || "EUR";

  useEffect(() => {
    setPageViewedProperties({
      page_type: "product",
    });
  }, [setPageViewedProperties]);

  useEffect(() => {
    if (currentShopType) {
      shopTypeDispatch({ type: "SET_SHOP_TYPE", payload: currentShopType });
      Cookies.set(OT_SHOP_TYPE_COOKIE, currentShopType);
    }
  }, [currentShopType, shopTypeDispatch]);

  useEffect(() => {
    if (!promoData || !promoData.promotion) {
      return;
    }

    const currentDate = new Date().getTime();
    const endDate = new Date(
      promoData.promotion.promo_end_date || ""
    ).getTime();

    promotionContextDispatch({
      type: "SET_PROMO",
      payload: {
        promotionDisplayType:
          promoData.promotion.promo_display_type ||
          PromotionDisplayType.Default,
        hasPromotionEnded: currentDate > endDate,
        promotionEndDate: promoData.promotion.promo_end_date || "",
        promotionMessage: promoData.promotion.promo_message || "",
        promotionStartDate: promoData.promotion.promo_start_date || "",
        promotion: promoData.promotion,
      },
    });
  }, [promoData, promotionContextDispatch]);

  useEffect(() => {
    if (productLoading) {
      setViewedProductState(ViewedProductState.LOADING);
    } else if (productError) {
      setViewedProductState(ViewedProductState.ERROR);
    } else if (product) {
      setViewedProductState(ViewedProductState.READY);
    }
  }, [productLoading, product, productError, setViewedProductState]);

  const currentGenderId =
    localeGenderId &&
    includes(product?.product_shop_type || [], localeGenderId) &&
    localeGenderId;

  const availableSizes: ProductVariation[] =
    product?.variations?.filter(
      (variation: ProductVariation) => variation.stock_quantity >= 0
    ) || [];

  const { formatVariationValue } = useSizeSchemaFilter({
    variations: product?.variations || [],
    selectedSchema: selectedSizeSchema,
    onSchemaChange: setSelectedSizeSchema,
  });

  const selectedSize =
    size?.size ||
    (availableSizes.length === 1 ? (availableSizes[0]?.size as string) : "");

  const hasLightningDealEnd =
    (promotionContextState.hasPromotionEnded &&
      promoData &&
      promoData.promotion.promo_display_type ===
        PromotionDisplayType.LightningDeal) ||
    false;

  const { increaseCartItem, loading, error } = useIncreaseCartItem({
    onCompleted: (cartData) => {
      if (product) {
        const allCurrentCartItem = cartData?.updateMyCart?.lineItems.filter(
          ({ variant }) =>
            getCTAttribute("woo_product_id", variant?.attributesRaw) ===
            product?.id
        );
        const currentCartItem = allCurrentCartItem?.at(-1);
        const sizeValue = getCTAttribute(
          "size_value",
          currentCartItem?.variant?.attributesRaw
        ) as string;

        const formattedSize = formatVariationValue(
          selectedVariationId.current || "",
          selectedSize || sizeValue,
          product.size_schema || undefined
        );

        setAddedProdToCart({
          productId: product.id,
          productName: product.name,
          productSlug: product.slug,
          brandName: product.brand.name,
          price: product.price,
          regularPrice: product.regular_price,
          image: product.images?.[0] as string,
          selectedSize: formattedSize,
          promoCode: !hasLightningDealEnd
            ? (promoData?.promotion.promo_code as string) || undefined
            : undefined,
          promotionDisplayType: !hasLightningDealEnd
            ? (promoData?.promotion
                .promo_display_type as PromotionDisplayType) || undefined
            : undefined,
        });
      }
    },
  });

  const productGenderId =
    currentGenderId ||
    (product && product.gender && localeFix({ gender: product.gender }));

  const sizeGuide = product?.size_guide?.find(
    ({ gender }) => gender === productGenderId
  );

  const hasSizeGuide = sizeGuide?.text ? true : false;

  const { segmentProductOutOfStock, segmentProductSizeSelected } = useSegment();

  useEffect(() => {
    if (product?.id) {
      timerID.current = window.setTimeout(() => {
        void trackProductEngagedEvent({ product, resultId });
      }, SEGMENT_ENGAGEMENT_TIMEOUT * 1000);
    }

    return () => {
      clearTimeout(timerID.current);
    };
  }, [product]);

  const isProductViewedTrackedRef = useRef("");

  useEffect(() => {
    if (!product || !product.id || isSessionLoading) {
      return;
    }

    if (
      pageViewTracked &&
      (!isProductViewedTrackedRef.current ||
        isProductViewedTrackedRef.current !== product.id)
    ) {
      void trackProductViewedEvent({
        product,
        isLoggedIn,
        email: session?.user?.email,
        resultId,
      });

      isProductViewedTrackedRef.current = product.id;

      return;
    }
  }, [
    product,
    isLoggedIn,
    isSessionLoading,
    pageViewTracked,
    pageViewedDispatch,
    session?.user?.email,
  ]);

  const prevRoute = useRef("");

  useEffect(() => {
    setSize(null);

    if (
      product?.id &&
      session?.user.email &&
      pageViewTracked &&
      prevRoute.current !== router.asPath
    ) {
      prevRoute.current = router.asPath;
      googleTagManager.trackProductPage(session?.user.email, product);
    }
  }, [product, pageViewTracked, session?.user.email, router.asPath]);

  const handleOpenSizeGuide = useCallback(() => setIsOpenSizeGuide(true), []);
  const handleCloseSizeGuide = useCallback(() => setIsOpenSizeGuide(false), []);

  const [addToCartError, setAddToCartError] = useState<boolean | undefined>();

  const handleCloseAddToCartError = useCallback(
    () => setAddToCartError(undefined),
    []
  );

  useEffect(() => {
    if (!!addToCartError && product?.id) {
      void segmentProductOutOfStock(product, selectedSize);
    }
  }, [addToCartError, product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error) {
      setAddToCartError(true);
    }
  }, [setAddToCartError, error]);

  const handleAddToCart = useCallback(
    async (variationId: string): Promise<boolean> => {
      if (!cart || isCartLoading) {
        return false;
      }

      const item = cart.lineItems.find(
        (lineItem) => lineItem?.variant?.sku === variationId
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const currentQuantity = item?.quantity || 0;
      const availableQuantity =
        item?.variant?.availability?.channels?.results.reduce(
          (sum, stockInfo) =>
            sum + Number(stockInfo.availability.availableQuantity),
          0
        );

      try {
        if (
          availableQuantity &&
          currentQuantity &&
          availableQuantity <= currentQuantity
        ) {
          setAddToCartError(true);
          return true;
        } else {
          selectedVariationId.current = variationId;

          const result = await increaseCartItem(
            variationId,
            item,
            getCartInfo(cart)
          );

          return !!result?.data;
        }
      } catch (e) {
        return false;
      }
    },
    [increaseCartItem, cart, isCartLoading]
  );

  useEffect(() => {
    if (availableSizes.length === 1 && product) {
      void segmentProductSizeSelected(product, selectedSize, resultId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (product && size) {
      void segmentProductSizeSelected(product, selectedSize, resultId);
    }
  }, [size]); // eslint-disable-line react-hooks/exhaustive-deps

  const productName = product?.name || "";
  const brandName = product?.brand?.name || "";
  const productImage = product?.images ? `${product.images[0]}` : "";
  // See docs/tech/responsive-images.md for why this is a capped JPEG.
  const socialShareImage = getLookbookImageUrl(productImage, {
    width: 1200,
    quality: 80,
    format: "jpeg",
  });

  // [TODO:Fix a bug with error handling and withApollo setup]
  // not only the page re-render due to mutation of the pageProps
  // but also the hook returns an error mutating from error to undefined
  // causing the page the render without data after multiple re-renders
  if (!productLoading && (!product || productError)) {
    return <ErrorPage />;
  }

  const desciption = t(
    i18n
  )`Shop ${productName} and other items from ${brandName} at Otrium with discounts up to 70%! Check out this item and other deals for great prices!`;

  const ribbonMessage = t(i18n)`Lightning Deal ends in`;

  const uppercaseBrandName = product?.brand?.name?.toUpperCase() || "";
  const noIndexationBrands = ["GANNI", "SUITSUPPLY"];

  const shouldIndex = !noIndexationBrands.includes(uppercaseBrandName);

  const openLoginRegisterModal = (
    actionSource: string,
    variationId?: string
  ) => {
    setActionSource(actionSource);
    setIsLoginRegisterModalOpen(true);
    setAddToCartVariationId(variationId);
  };

  return (
    <StyledProductView
      className={styles.root}
      data-cnstrc-product-detail
      data-cnstrc-item-id={product?.id}
      data-cnstrc-item-name={product?.name}
      // TODO: post-cnstrctr-integration — Constructor catalog (event-engine
      // mapProductToConstructor) does not emit a variations field, so the CT
      // variant id has nothing to reconcile against. Re-enable with the
      // matching id format once the catalog mapper publishes variations.
      data-cnstrc-item-price={product?.price}
    >
      <PageHead
        title={t(i18n)`${
          product?.name ? `${product.name} | ` : ""
        }Online Outlet | Otrium`}
        deeplink={`otrium://product/${slug}`}
        description={desciption}
        isPDP={shouldIndex}
        seoContent={seoContent}
        metaTags={
          <>
            <meta name="product_id" content={product?.id} />
            {/* Twitter */}
            <meta
              name="twitter:title"
              content={`${productName} | ${brandName} | Otrium`}
            />
            <meta name="twitter:card" content="product" />
            <meta name="twitter:image" content={socialShareImage} />
            <meta
              name="twitter:data1"
              content={product?.price ? `${product.price} ${currency}` : ""}
            />
            <meta name="twitter:label1" content={t(i18n)`Price`} />
            {/* Open Graph / Facebook */}
            <meta
              property="og:title"
              content={`${productName} | ${brandName} | Otrium`}
            />
            <meta property="og:url" content={`otrium://product/${slug}`} />
            <meta name="og:image" content={socialShareImage} />
            <meta property="og:site_name" content="Otrium Shop"></meta>
            <meta property="og:type" content="product" />
          </>
        }
      />
      <main className={styles.content}>
        <div className="product-details">
          {productGenderId ? (
            <div className="product-breadcrumb-wrapper">
              <ProductBreadcrumbs
                product={product}
                genderId={productGenderId}
                locale={locale}
              />
            </div>
          ) : null}

          {product ? (
            <ImageCarouselContextProvider>
              <ProductPhotos
                key={product.id}
                photos={product.images || []}
                labels={product.labels || []}
                productName={product.name}
                productId={product.id}
              />
            </ImageCarouselContextProvider>
          ) : (
            <Skeleton />
          )}

          {product ? (
            <div className="ProductView__productSidebar">
              <ProductInfo
                labels={product.labels || []}
                englishGenderId={productGenderId as ProductGender}
                productName={product.name}
                brandName={product.brand.name}
                brandSlug={product.brand.slug}
                price={product.price}
                regularPrice={product.regular_price}
                lowestPrice={product.lowest_price}
                promoCode={
                  promoData?.promotion.promo_display_type ===
                  PromotionDisplayType.LightningDeal
                    ? (promoData.promotion.promo_code as string)
                    : undefined
                }
              />

              {product && !productError && promoData && promoData.promotion ? (
                <>
                  {promoData.promotion.promo_display_type ===
                  PromotionDisplayType.LightningDeal ? (
                    <PromotionalRibbon
                      icon={<Flash />}
                      message={ribbonMessage}
                      promotion={promoData.promotion}
                    />
                  ) : null}
                  {promoData.promotion.promo_display_type ===
                  PromotionDisplayType.FlashSale ? (
                    <PromoTimer
                      promotion={promoData.promotion}
                      margins={{
                        marginBlockEnd: "space0",
                        marginBlockStart: "space0",
                      }}
                    />
                  ) : null}
                  <PromotionalBlock promotion={promoData.promotion} />
                </>
              ) : null}
              <ProductOrder
                key={product.slug}
                className="ProductView__productOrder"
                product={product}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onAddToCart={handleAddToCart}
                addToCartLoading={loading || isCartLoading}
                handleSizeChange={setSize}
                hasSizeGuide={hasSizeGuide}
                hasAddToWishlistButton
                onOpenSizeGuide={handleOpenSizeGuide}
                sizeGuideRef={sizeGuideRef}
                withMobileOverlay
                customerType={
                  cart?.id ? "existing" : cart === null ? "new" : "guest"
                }
                openLoginRegisterModal={openLoginRegisterModal}
                selectedSizeSchema={selectedSizeSchema}
                onSchemaChange={setSelectedSizeSchema}
                resultId={resultId}
              />
              <ProductMeta
                key={product.id}
                className="ProductView__productMeta"
                data-testid="product-view-meta"
                locale={locale}
                description={product.description}
                material={product.material as string}
                brand={product.brand}
                shippingText={product?.shipping?.shipping_text}
                paymentTypes={product?.payment_types}
                fitSize={product.fit_size}
                careInstructions={product.care_instructions}
                goodOnYouInfo={product.brand.good_on_you_info}
                openLoginRegisterModal={openLoginRegisterModal}
              />
            </div>
          ) : (
            <ProductSidebarSkeleton className="ProductView__productSidebar" />
          )}
          {product && (
            <AddToCollection productsIds={[parseInt(product.id, 10)]} />
          )}
        </div>
      </main>
      {product && (
        <ProductViewCarousels
          slug={product.slug}
          shopType={(currentShopType as ProductGender) ?? "women"}
        />
      )}
      <NoSSR>
        <div className={styles.modals}>
          {product && (
            <>
              <Modal isOpen={isOpenSizeGuide} onClose={handleCloseSizeGuide}>
                <SizeGuide
                  sizeGuide={sizeGuide?.text || ""}
                  onClose={handleCloseSizeGuide}
                  ref={sizeGuideRef}
                />
              </Modal>
              <Modal
                isOpen={!!addToCartError}
                withHeader={true}
                onClose={handleCloseAddToCartError}
              >
                <OutOfStock
                  genderId={productGenderId as ProductGender}
                  brandSlug={product.brand.slug}
                  onClose={handleCloseAddToCartError}
                />
              </Modal>
            </>
          )}
        </div>
      </NoSSR>
      {isLoginRegisterModalOpen && (
        <LoginRegisterModal
          open={isLoginRegisterModalOpen}
          onClose={() => {
            setIsLoginRegisterModalOpen(false);
          }}
          redirectTo={path}
          trackingData={{
            pageName: GATED_PRODUCT_PAGE,
            shopType: localeGenderId as ShopType,
            actionSource,
            productName: product?.name,
            productId: product?.id,
            level: product?.gate?.level || "undefined",
            redirectTo: path,
          }}
          onRedirect={(type) => {
            deleteSessionStorageItem(GATED_POPUP_REDIRECT_DATA);
            setSessionStorageJSON(GATED_POPUP_REDIRECT_DATA, {
              gatedActionType: type,
              addToCartData: {
                variationId: addToCartVariationId,
                productId: product?.id,
                product,
              },
              actionSource,
            });
          }}
        />
      )}
    </StyledProductView>
  );
};

export default ProductView;
